import { isHomepage, type PageProductInfo, type PageType } from '@teo/widget-shared';
import { currentUrlMatches, normalizeEntityUrl, normalizeName, type EvidenceContext, type PageDecision, type PageEvidence } from './evidence.js';

type Group = { id: string; evidence: PageEvidence[] };

function incompatible(a: PageEvidence, b: PageEvidence, url: string): boolean {
  const aUrl = normalizeEntityUrl(a.entityUrl, url);
  const bUrl = normalizeEntityUrl(b.entityUrl, url);
  if (aUrl && bUrl && aUrl !== bUrl) return true;
  if (a.identity && b.identity && a.identity.origin === b.identity.origin && a.identity.kind === b.identity.kind && a.identity.value !== b.identity.value) return true;
  return false;
}

function compatible(a: PageEvidence, b: PageEvidence, url: string): boolean {
  if (incompatible(a, b, url)) return false;
  const aUrl = normalizeEntityUrl(a.entityUrl, url);
  const bUrl = normalizeEntityUrl(b.entityUrl, url);
  if (aUrl && bUrl && aUrl === bUrl) return true;
  if (a.identity && b.identity && a.identity.origin === b.identity.origin && a.identity.kind === b.identity.kind && a.identity.value === b.identity.value) return true;
  const aName = normalizeName(a.name);
  const bName = normalizeName(b.name);
  return Boolean(aName && bName && aName === bName);
}

function groupsOf(evidence: PageEvidence[], url: string): Group[] {
  const rows = evidence.filter(row => row.active && row.product && !row.hidden);
  const groups: Group[] = [];
  for (const row of rows) {
    const group = groups.find(item => item.evidence.some(existing => compatible(existing, row, url)) &&
      item.evidence.every(existing => !incompatible(existing, row, url)));
    if (group) group.evidence.push(row);
    else groups.push({ id: row.evidenceId, evidence: [row] });
  }
  for (let left = 0; left < groups.length; left += 1) {
    for (let right = left + 1; right < groups.length;) {
      const a = groups[left]!;
      const b = groups[right]!;
      if (a.evidence.some(first => b.evidence.some(second => compatible(first, second, url))) &&
        a.evidence.every(first => b.evidence.every(second => !incompatible(first, second, url)))) {
        a.evidence.push(...b.evidence);
        groups.splice(right, 1);
      } else right += 1;
    }
  }
  return groups;
}

function productOf(group: Group): PageProductInfo {
  const product: PageProductInfo = {};
  for (const row of group.evidence) {
    for (const [key, value] of Object.entries(row.product ?? {})) {
      if (value != null && value !== '' && product[key as keyof PageProductInfo] == null) {
        (product as Record<string, unknown>)[key] = value;
      }
    }
  }
  return product;
}

function decision(type: PageType, reason: string, evidence: PageEvidence[], confidence: PageDecision['confidence'], groups: Group[], group?: Group): PageDecision {
  return { type, confidence, reason, mainEntityId: group?.id, evidenceIds: (group?.evidence ?? evidence).map(row => row.evidenceId),
    product: group ? productOf(group) : undefined,
    listItems: groups.filter(item => item.evidence.some(row => row.scope === 'list-item')).map(productOf) };
}

function hasDifferentUrl(group: Group, context: EvidenceContext): boolean {
  return group.evidence.some(row => row.entityUrl &&
    !currentUrlMatches(row.entityUrl, context.url) && !currentUrlMatches(row.entityUrl, context.canonicalUrl ?? ''));
}

function mainDomSignals(group: Group, context: EvidenceContext): number {
  const rows = group.evidence;
  const mainCommerce = rows.some(row => (row.kind === 'structured-product' || row.kind === 'microdata-product') &&
    row.scope !== 'list-item' && !row.inItemList && !row.inRepeatedCard);
  if (!mainCommerce) return 0;
  const dom = rows.filter(row => (row.kind === 'dom-product' || row.kind === 'microdata-product') && row.scope === 'main-entity' && row.inMain && !row.inRepeatedCard && !row.inCarousel && !row.inItemList);
  if (!dom.length || hasDifferentUrl(group, context)) return 0;
  const h1 = dom.some(row => row.h1Match);
  const purchase = dom.some(row => row.purchaseAction);
  const canonical = Boolean(context.canonicalUrl && rows.some(row => row.entityUrl && currentUrlMatches(row.entityUrl, context.canonicalUrl!)));
  const structured = rows.some(row => row.structuredMetadata && (row.kind === 'structured-product' || row.kind === 'microdata-product'));
  const typedId = rows.some(a => a.identity && (a.kind === 'structured-product' || a.kind === 'microdata-product') &&
    dom.some(b => b !== a && b.identity && b.identity.kind === a.identity!.kind && b.identity.value === a.identity!.value));
  return Number(h1) + Number(purchase) + Number(canonical) + Number(structured) + Number(typedId);
}

/** The only module that assigns final page type and page.product. */
export function resolvePageEvidence(context: EvidenceContext, evidence: PageEvidence[]): PageDecision {
  const active = evidence.filter(row => row.active && !row.hidden);
  const groups = groupsOf(active, context.url);
  const root = isHomepage({ pathname: context.pathname, doc: context.doc });

  for (const event of active.filter(row => row.kind === 'detail-event' && row.scope !== 'list-item')) {
    const items = active.filter(row => row.kind === 'detail-event' && row.evidenceId.startsWith(event.evidenceId.replace(':event', ':item:')));
    if (items.length !== 1) continue;
    const group = groups.find(item => item.evidence.includes(items[0]!));
    if (group && !hasDifferentUrl(group, context)) return decision('product', 'current_detail_event', active, 'high', groups, group);
  }

  for (const group of groups) {
    if (group.evidence.some(row => (row.kind === 'structured-product' || row.kind === 'microdata-product') &&
      row.entityUrl && currentUrlMatches(row.entityUrl, context.url))) {
      return decision('product', 'url_matched_product', active, 'high', groups, group);
    }
  }

  if (!root) {
    for (const group of groups) {
      if (group.evidence.some(row => row.kind === 'structured-product' || row.kind === 'microdata-product') && mainDomSignals(group, context) >= 2) {
        return decision('product', 'main_dom_product', active, 'medium', groups, group);
      }
    }
  }

  if (root) return decision('home', 'root_home', active.filter(row => row.source === 'url-heuristics'), 'high', groups);

  const search = active.find(row => row.pageHint === 'search' && row.scope === 'page');
  if (search) return decision('search', 'current_search', [search], 'medium', groups);

  const list = active.find(row => row.kind === 'list-event' && row.scope === 'page' && active.some(item => item.kind === 'list-event' && item.scope === 'list-item'))
    ?? active.find(row => row.kind === 'commerce-item-list');
  if (list) return decision('category', 'current_commerce_list', [list], 'high', groups);
  const cards = active.filter(row => row.kind === 'product-card' && row.inRepeatedCard);
  if (cards.length >= 2) return decision('category', 'repeated_product_cards', cards, 'medium', groups);
  const collectionHint = active.find(row => row.pageHint === 'category' && row.source !== 'url-heuristics');
  if (collectionHint && active.some(row => row.scope === 'list-item')) return decision('category', 'collection_page_metadata', [collectionHint], 'medium', groups);

  for (const type of ['cart', 'checkout', 'blog'] as const) {
    const hint = active.find(row => row.pageHint === type);
    if (hint) return decision(type, `current_${type}`, [hint], 'medium', groups);
  }
  return decision('other', 'insufficient_evidence', active, 'low', groups);
}
